/**
 * Manage order details service
 *
 * @author Ryan Sweeney <rsweeney@buildcentrix.com>
 * @copyright (c) 2024 Build Centrix
 */
import { Inject, Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { WdLocalSharedFunctionsService } from '../../shared/wd-local-shared-functions.service';
import { WdUserService } from '../../users/wd-users.service';
import { WdUserGroupService } from '../../users/groups/wd-user-groups.service';
import { WdGraphQlService } from '../../graphql/wd-graphql.service';
import {
  IOrderAdjustmentModel,
  IOrderDetailsOptionsModel,
  IOrderExportModel,
  IOrderItemModel,
  ITrackerItemModel,
} from '../../../interfaces/orders/order-interfaces';
import { MatTableDataSource } from '@angular/material/table';
import { SelectionModel } from '@angular/cdk/collections';
import { WdLocalEnvironmentService } from '../../environment/wd-local-environment.service';
import { WdWorkOrderReportService } from '../reports/wd-work-order-report.service';
import { WdOrderMessageService } from '../messages/wd-order-message.service';
import { WdOrdersV2Service } from '../wd-orders-v2.service';
import { WdOrderStatusService } from '../statuses/wd-order-status.service';
import { WdOrderShippingLoadingZonesService } from '../options/shipping/wd-order-shipping-loading-zones.service';
import { WdOrderShippingOptionsService } from '../options/shipping/wd-order-shipping-options.service';
import {
  IBcxDateTimeHelper,
  IBcxErrorService,
  IBcxOauth2HttpService,
  IBcxSharedFunctionsService,
  IBcxStorageService,
} from '../../../interfaces/generic/generic-interfaces';
import { IVehicleAssignmentModel } from '../../../interfaces/vehicles/vehicle-interfaces';
import { WdVehicleAssignmentService } from '../../vehicles/assignments/wd-vehicle-assignment.service';

@Injectable({
  providedIn: 'root',
})
export class WdManageOrdersService {
  /**
   * Constructor Initialization
   * @since 4.71.0
   * @memberof WdManageOrdersService
   */
  constructor(
    private wdEnvironmentService: WdLocalEnvironmentService,
    private wdTranslate: TranslateService,
    public wdGraphQlService: WdGraphQlService,
    public wdLocalSharedFunctionsService: WdLocalSharedFunctionsService,
    public wdOrdersV2Service: WdOrdersV2Service,
    public wdOrderMessageService: WdOrderMessageService,
    public wdOrderReportService: WdWorkOrderReportService,
    public wdOrderStatusService: WdOrderStatusService,
    public wdOrderShippingOptionsService: WdOrderShippingOptionsService,
    public wdOrderShippingLoadingZonesService: WdOrderShippingLoadingZonesService,
    public wdUserService: WdUserService,
    public wdUserGroupService: WdUserGroupService,
    public wdVehicleAssignmentService: WdVehicleAssignmentService,
    @Inject('wdDateTimeHelper') public wdDateTimeHelper: IBcxDateTimeHelper,
    @Inject('wdErrorService') public wdErrorService: IBcxErrorService,
    @Inject('wdOauth2HttpService')
    public wdOauth2HttpService: IBcxOauth2HttpService,
    @Inject('wdSharedFunctionsService')
    public wdSharedFunctionsService: IBcxSharedFunctionsService,
    @Inject('wdStorageService') public wdStorageService: IBcxStorageService,
  ) {}

  // Booleans
  isLoadingOrder = new BehaviorSubject(true); // Loading Single Order
  isReloadingOrder = new BehaviorSubject(false); // Reloading Single Order

  // Data Required for Editing Order Details
  orderDetailsData: IOrderDetailsOptionsModel = {
    users: new BehaviorSubject<Array<object>>([]),
    userGroups: new BehaviorSubject<Array<object>>([]),
  };

  /**
   * Build Fitting, Simple Product or Product with Attribute for UI
   * @memberof WdManageOrdersService
   */
  private buildFittingOrProductItemForDisplay(item: any) {
    return {
      id: this.wdSharedFunctionsService.getDescendantProperty(item, 'id'),
      label: this.wdSharedFunctionsService.getDescendantProperty(
        item,
        'order.label',
      ),
      attributes: this.wdSharedFunctionsService.getDescendantProperty(
        item,
        'engine.inputs.user.attributes',
      ),
      dimensions: this.wdSharedFunctionsService.getDescendantProperty(
        item,
        'engine.inputs.user.dimensions',
      ),
      group: this.wdSharedFunctionsService.getDescendantProperty(
        item,
        'order.fulfillmentGroup',
      ),
      insulations: this.wdSharedFunctionsService.getDescendantProperty(
        item,
        'engine.output.insulations',
      ),
      joints: this.wdSharedFunctionsService.getDescendantProperty(
        item,
        'engine.output.joints',
      ),
      holes: this.wdSharedFunctionsService.getDescendantProperty(
        item,
        'engine.inputs.user.holes',
      ),
      materials: this.wdSharedFunctionsService.getDescendantProperty(
        item,
        'engine.output.materials',
      ),
      notes: this.wdSharedFunctionsService.getDescendantProperty(
        item,
        'order.notes',
      ),
      quantity: this.wdSharedFunctionsService.getDescendantProperty(
        item,
        'order.quantity',
      ),
      referenceNumber: this.wdSharedFunctionsService.getDescendantProperty(
        item,
        'engine.inputs.user.referenceNumber',
      ),
      shipping: {
        status: this.wdSharedFunctionsService.getDescendantProperty(
          item,
          'order.shipping.status',
        ),
      },
      specifications: this.wdSharedFunctionsService.getDescendantProperty(
        item,
        'engine.output.specifications',
      ),
      tag: this.wdSharedFunctionsService.getDescendantProperty(
        item,
        'order.tag',
      ),
      type: this.wdSharedFunctionsService.getDescendantProperty(
        item,
        'order.type',
      ),
    };
  }

  /**
   * Build Tracker Item Attribute for UI
   * @since 4.71.0
   * @memberof WdManageOrdersService
   */
  private buildTrackerItemForDisplay(item: any) {
    return {
      id: this.wdSharedFunctionsService.getDescendantProperty(item, '_id'),
      label: this.wdSharedFunctionsService.getDescendantProperty(
        item,
        'item.labels.name',
      ),
      notes: this.wdSharedFunctionsService.getDescendantProperty(
        item,
        'item.notes',
      ),
      quantity: this.wdSharedFunctionsService.getDescendantProperty(
        item,
        'item.quantity',
      ),
      tag: this.wdSharedFunctionsService.getDescendantProperty(
        item,
        'item.tag',
      ),
    };
  }

  /**
   * Get data required to edit order details
   *
   * @since 4.71.0
   * @memberof WdManageOrdersService
   */
  async fetchRequiredOrderDetailsData(): Promise<void> {
    // Data required for order details
    let users: Array<any> = [];
    let userGroups: Array<any> = [];

    try {
      // Set Query
      const query = {
        query:
          'query{ users { id, firstName, lastName }, userGroups { id, label }}',
      };
      // Get Users Data
      const response = await this.wdGraphQlService.fetchGraphQlData(query);
      // Set Users
      users = this.wdUserService.parseUsers(response.users);
      userGroups = this.wdUserGroupService.parseUserGroups(response.userGroups);
    } catch (error) {
      // Error Message
      this.wdErrorService.userError(
        this.wdTranslate.instant('labels.error'),
        this.wdTranslate.instant('outputs.failedToLoad', {
          type: this.wdTranslate.instant('order.title.singular'),
        }),
      );
    } finally {
      // Set Data
      this.orderDetailsData.users.next(users);
      this.orderDetailsData.userGroups.next(userGroups);
    }
  }

  /**
   * GET order based on object index
   *
   * @since 4.71.0
   * @memberof WdManageOrdersService
   */
  async getOrderByIndex(index: string, showLoader = true): Promise<any> {
    // Toggle Loader
    showLoader
      ? this.isLoadingOrder.next(true)
      : this.isReloadingOrder.next(true);
    try {
      const params: Record<string, any> = {
        idType: 'indexId',
        getMessages: true,
      };
      const response = await this.wdOauth2HttpService.get(
        `${this.wdLocalSharedFunctionsService.orderApiUrl}orders/${index}` +
          `?${new URLSearchParams(params)}`,
      );
      return this.parseOrderFromOrderApi(response.data);
    } catch (error) {
      // Error Message
      this.wdErrorService.userError(
        this.wdTranslate.instant('labels.error'),
        this.wdTranslate.instant('outputs.failedToLoad', {
          type: this.wdTranslate.instant('order.title.singular'),
        }),
      );
      throw error;
    } finally {
      // Toggle Loader
      this.isLoadingOrder.next(false);
      this.isReloadingOrder.next(false);
    }
  }

  /**
   * Get Fulfillment Exports
   * @since 4.71.0
   * @memberof WdManageOrdersService
   */
  private getFulfillmentExports(
    index: string,
    groupIndex: string,
  ): Array<IOrderExportModel> {
    const results = [];
    // Fulfillment PDF
    if (this.wdEnvironmentService.permissions.canViewFulfillmentPdfExport) {
      results.push({
        icon: 'pdf',
        label: this.wdTranslate.instant('exports.fulfillment_pdf'),
        target: '_blank',
        url: this.wdOrderReportService.getOrderReportUrl(
          index,
          'fulfillment',
          groupIndex,
        ),
      });
    }
    // Fulfillment CSV
    if (this.wdEnvironmentService.permissions.canViewFulfillmentCsvExport) {
      results.push({
        icon: 'csv',
        label: this.wdTranslate.instant('exports.fulfillment_csv'),
        target: '_blank',
        url: this.wdOrderReportService.getOrderReportUrl(
          index,
          'csv',
          groupIndex,
        ),
      });
    }
    return results;
  }

  /**
   * Get matching status value from options
   * @since 4.71.0
   * @memberof WdManageOrdersService
   */
  private getMatchingStatusValue(
    status: any,
    statusKey: string,
    matchKey: string,
    statusOptions: Array<any>,
  ) {
    const match = this.wdSharedFunctionsService.getArrayObjectByKey(
      status[statusKey],
      matchKey,
      statusOptions,
    );
    const hasMatch = !!match[matchKey];

    if (hasMatch) {
      status = match;
      status.hasMatchingStatusOption = statusOptions.indexOf(status) > -1;
    } else {
      status = statusOptions.length > 0 ? null : status;
    }

    return status;
  }

  /**
   * Parse Order from Order API
   *
   * @memberof WdManageOrdersService
   */
  parseOrderFromOrderApi(order: any): object {
    const result = order;
    const calculations = this.wdSharedFunctionsService.getDescendantProperty(
      order,
      'calculations.totals.current',
    );

    // Calculations
    if (calculations) {
      const fabrication = this.wdSharedFunctionsService.getDescendantProperty(
        calculations,
        'shopLabour.value',
      );
      const price = this.wdSharedFunctionsService.getDescendantProperty(
        calculations,
        'price.value',
      );
      const weight = this.wdSharedFunctionsService.getDescendantProperty(
        calculations,
        'weight.value',
      );
      result.calculations.totals.current.shopLabour.value = WdLocalSharedFunctionsService.convertFloatHoursToHoursAndMinutes(
        fabrication,
        'long',
      );
      result.calculations.totals.current.price.value = price.toFixed(2);
      result.calculations.totals.current.weight.value = weight.toFixed(2);
    }

    // Dates
    if (result.ordered) {
      const orderedDate = this.wdSharedFunctionsService.getDescendantProperty(
        result,
        'ordered',
      );
      result.ordered = new Date(orderedDate);
    }
    if (result.requested) {
      const requestedDate = this.wdSharedFunctionsService.getDescendantProperty(
        result,
        'requested',
      );
      result.requested = new Date(requestedDate);
    }
    if (result.production) {
      const productionDate = this.wdSharedFunctionsService.getDescendantProperty(
        result,
        'production.date',
      );
      const productionEndDate = this.wdSharedFunctionsService.getDescendantProperty(
        result,
        'production.endDate',
      );

      result.production.date = new Date(productionDate);
      if (productionEndDate) {
        result.production.endDate = new Date(productionEndDate);
      }
    }
    if (result.delivery) {
      const shippingDate = this.wdSharedFunctionsService.getDescendantProperty(
        result,
        'delivery.date',
      );
      result.delivery.date = new Date(shippingDate);
    }

    // Messages
    if (result.messages) {
      const messages = this.wdSharedFunctionsService.getDescendantProperty(
        result,
        'messages',
      );
      result.messages = this.wdOrderMessageService.parseOrderMessages(messages);
    }

    // Items
    if (result.items) {
      result.items.forEach((item: any) => {
        if (item.order) {
          // Item Shipping Status
          const shippingStatus = this.wdSharedFunctionsService.getDescendantProperty(
            item,
            'order.shipping.status',
          );
          const options = this.wdOrderStatusService
            .orderItemShippingStatusOptions.value;
          if (shippingStatus) {
            // Get matching shipping status
            const statusId = item.order.shipping.status.id;
            const match = this.wdSharedFunctionsService.getArrayObjectByKey(
              statusId,
              'id',
              options,
            );
            const hasMatch = !!match.id;
            item.order.shipping.status =
              options && hasMatch ? match : item.order.shipping.status;
          } else {
            // Default to first status if none found
            item.order.shipping = {
              status: options.find((status) => status.sort === 1),
            };
          }
          // Existing Status Not Found (Set Label)
          if (!item.order.shipping.status) {
            item.order.shipping.status = {
              label: 'Status Not Set',
            };
          }
          // Flag for Missing Status
          item.order.shipping.status.hasMatchingStatusOption =
            options.indexOf(item.order.shipping.status) > -1;
        }
      });
    }

    // Set labels (user and jobs)
    this.wdOrdersV2Service.setOrderLabels(result);

    // Fulfillment Group
    if (result.fulfillment) {
      if (result.fulfillment.groups) {
        result.fulfillment.groups.forEach((group: any) => {
          const groupAdjustmentsResult: Array<IOrderAdjustmentModel> = [];
          const groupFittingsResult: Array<IOrderItemModel> = [];
          const groupProductsResult: Array<IOrderItemModel> = [];
          const trackerItemResult: Array<ITrackerItemModel> = [];
          const adjustmentItems = this.wdSharedFunctionsService.getDescendantProperty(
            order,
            'adjustments.lineItem.items',
          );

          // Order Adjustments
          if (adjustmentItems) {
            adjustmentItems.forEach((item: any) => {
              const groupIndex = this.wdSharedFunctionsService.getDescendantProperty(
                group,
                'index',
              );
              const adjustmentIndex = this.wdSharedFunctionsService.getDescendantProperty(
                item,
                'fulfillment.group.index',
              );
              if (adjustmentIndex === groupIndex) {
                groupAdjustmentsResult.push(item);
              }
            });
          }
          // Order Items
          if (result.items) {
            result.items.forEach((item: any) => {
              // Fulfillment Group Matching
              const fulfillmentGroupIndex = this.wdSharedFunctionsService.getDescendantProperty(
                group,
                'index',
              );
              const orderItemFulfillmentGroupIndex = this.wdSharedFunctionsService.getDescendantProperty(
                item,
                'order.fulfillmentGroup.index',
              );
              const fulfillmentGroupIndexesMatch =
                fulfillmentGroupIndex === orderItemFulfillmentGroupIndex;

              // Check for tracker items
              const trackerModule = 'tracker';
              const groupModule = this.wdSharedFunctionsService.getDescendantProperty(
                group,
                'module.id',
              );
              const itemModule = this.wdSharedFunctionsService.getDescendantProperty(
                item,
                'module',
              );
              const groupModuleIsTracker = groupModule === trackerModule;
              const itemModuleIsTracker = itemModule === trackerModule;

              // Add items to correct array
              if (
                item.order.type === 'fitting' &&
                fulfillmentGroupIndexesMatch
              ) {
                // Add fitting to fitting array
                groupFittingsResult.push(
                  this.buildFittingOrProductItemForDisplay(item),
                );
              } else if (
                item.order.type === 'simple' &&
                fulfillmentGroupIndexesMatch
              ) {
                // Add simple product to products array
                groupProductsResult.push(
                  this.buildFittingOrProductItemForDisplay(item),
                );
              } else if (
                item.order.type === 'attribute' &&
                fulfillmentGroupIndexesMatch
              ) {
                // Add product with attributes to products array
                groupProductsResult.push(
                  this.buildFittingOrProductItemForDisplay(item),
                );
              } else if (groupModuleIsTracker && itemModuleIsTracker) {
                // Add tracker item to tracker items array
                trackerItemResult.push(this.buildTrackerItemForDisplay(item));
              } else {
                // Unknown item type (tracker items)
                // @todo Fix this See https://webduct.atlassian.net/browse/ORAPI-247
              }
            });
          }
          group.adjustmentsUpdating = false;
          group.adjustmentsDataSource = new MatTableDataSource(
            groupAdjustmentsResult,
          );
          group.exports = this.getFulfillmentExports(order.index, group.index);
          group.fittingsDataSource = new MatTableDataSource(
            groupFittingsResult,
          );
          group.fittingsSelection = new SelectionModel(true, [] as any);
          group.fittingsUpdating = false;
          group.productsDataSource = new MatTableDataSource(
            groupProductsResult,
          );
          group.productsSelection = new SelectionModel(true, [] as any);
          group.productsUpdating = false;
          group.trackerItemsDataSource = new MatTableDataSource(
            trackerItemResult,
          );
          group.trackerItemsSelection = new SelectionModel(true, [] as any);
          group.trackerItemsUpdating = false;
          group.status = this.getMatchingStatusValue(
            group.status || {},
            'index',
            'id',
            this.wdOrderStatusService.productionStatusOptions.value,
          );
        });
      }
    }

    // Overall Status
    if (result.status) {
      // Set current status as only option if none available
      if (this.wdOrderStatusService.overallStatusOptions.value.length === 0) {
        this.wdOrderStatusService.overallStatusOptions.next([result.status]);
      }
      result.status = this.getMatchingStatusValue(
        result.status,
        'id',
        'id',
        this.wdOrderStatusService.overallStatusOptions.value,
      );
    }

    // Production Status
    if (result.production) {
      if (result.production.status) {
        result.production.status = this.getMatchingStatusValue(
          result.production.status,
          'index',
          'id',
          this.wdOrderStatusService.productionStatusOptions.value,
        );
      }
    }

    // Shipping
    if (result.delivery) {
      // Status
      if (result.delivery.status) {
        const statusIndex = result.delivery.status.index;
        const options = this.wdOrderStatusService.shippingStatusOptions.value;
        const match = this.wdSharedFunctionsService.getArrayObjectByKey(
          statusIndex,
          'id',
          options,
        );
        const hasMatch = !!match.id;
        result.delivery.status =
          options && hasMatch ? match : result.delivery.status;
        // Flag for Missing Status
        result.delivery.status.hasMatchingStatusOption =
          options.indexOf(result.delivery.status) > -1;
      }
      // Option
      if (result.delivery.option) {
        // Get the active and archived shipping options from the service
        const activeShippingOptions = this.wdOrderShippingOptionsService
          .shippingOptionsActive.value;
        const archivedShippingOptions = this.wdOrderShippingOptionsService
          .shippingOptionsArchived.value;

        // Try to find the selected shipping option in the active options
        const activeShippingOption = activeShippingOptions.find(
          (option) => option.value === result.delivery.option,
        );

        // If the selected shipping option was not found in active options
        if (!activeShippingOption?.id) {
          // Try to find the selected shipping option in the archived options
          const archivedShippingOption = archivedShippingOptions.find(
            (option) => option.value === result.delivery.option,
          );

          // Set the selected shipping option to the archived option or an empty object if not found
          result.delivery.option = archivedShippingOption || {};

          // Add the archived option to the dropdown list of active options
          this.wdOrderShippingOptionsService.shippingOptionsActive.next(
            activeShippingOptions.concat(result.delivery.option),
          );
        } else {
          // Set the selected shipping option to the found active option
          result.delivery.option = activeShippingOption;
        }
      }

      if (result.delivery.loadingZone) {
        result.delivery.loadingZone = this.wdSharedFunctionsService.getArrayObjectByKey(
          result.delivery.loadingZone.id,
          'id',
          this.wdOrderShippingLoadingZonesService.loadingZoneOptions.value,
        );
      }

      if (result.delivery) {
        result.delivery.vehicle = {};
        // Get all assignments
        const allAssignments: Array<IVehicleAssignmentModel> = this
          .wdVehicleAssignmentService.vehicleAssignmentOptions.value;

        // Get valid vehicle assignments for each order based on order shipping date
        const options: Array<IVehicleAssignmentModel> = this.wdLocalSharedFunctionsService.getAssignmentsByShippingDate(
          allAssignments,
          new Date(order.delivery?.date),
        );
        // Get currently selected vehicle assignment object
        const selectedAssignments: Array<IVehicleAssignmentModel> = this.wdLocalSharedFunctionsService.getAssignmentsByOrderId(
          allAssignments,
          order.id,
        );
        result.delivery.vehicle.assignments = this.wdOrdersV2Service.setAssignmentLabels(
          selectedAssignments,
        );
        result.delivery.vehicle.assignmentOptions = [
          ...this.wdOrdersV2Service.setAssignmentLabels(options),
        ];
      }
    }

    // Model View Link
    result.modelLink = this.wdLocalSharedFunctionsService.getModelViewerUrl(
      order.index,
    );

    // Exports and Reports
    result.exports = this.wdOrdersV2Service.getOrderExports(order.index);
    result.reports = this.wdOrdersV2Service.getOrderReports(order.index);
    result.downloads = this.wdOrdersV2Service.getOrderDownloads(order.index);

    return result;
  }
}
